import { useState } from "react";
import { useLessonsForNavigation } from "../lessons/useLessonsForNavigation";
import { useProjectsForNavigation } from "../projects/useProjectsForNavigation";

interface NavigationItem {
  id: string;
  name: string;
}

/**
 * Hook for managing hierarchical navigation through projects in modals.
 *
 * Provides state and methods for navigating through a tree structure of projects,
 * including breadcrumb navigation, search functionality, and loading states.
 * Used primarily in modals like DuplicateItemModal and MoveModal where users need
 * to navigate through the project hierarchy to select a destination parent.
 *
 * Lessons are also fetched for display purposes but are not navigable
 * (they serve as visual context for the user).
 *
 * @param searchText - Current search query string
 * @returns Navigation state and control methods
 *
 * @example
 * const {
 *   currentProjectId,
 *   projects,
 *   lessons,
 *   loading,
 *   navigationStack,
 *   handleProjectClick,
 *   handleNavigateBack,
 *   resetNavigation
 * } = useProjectNavigation(searchText);
 */
export function useProjectNavigation(
  searchText: string,
  initialStack?: NavigationItem[],
  projectLimit?: number
) {
  const [currentProjectId, setCurrentProjectId] = useState<string | null>(
    initialStack?.at(-1)?.id ?? null
  );
  const [navigationStack, setNavigationStack] = useState<NavigationItem[]>(initialStack ?? []);

  // Fetch projects for current navigation level
  const {
    projects,
    loading: projectsLoading,
    hasMore: hasMoreProjects,
    loadMore: loadMoreProjects,
  } = useProjectsForNavigation({
    parentId: currentProjectId,
    searchText,
    limit: projectLimit,
  });

  // Fetch lessons for display (not navigable)
  const {
    lessons,
    loading: lessonsLoading,
    hasMore: hasMoreLessons,
    loadMore: loadMoreLessons,
  } = useLessonsForNavigation({
    parentId: currentProjectId,
    searchText,
  });

  const isEmpty = projects.length === 0 && lessons.length === 0;

  const currentParent = navigationStack[navigationStack.length - 1];

  /**
   * Navigate into a project folder or select it as destination
   * Pass null to select root level as destination
   */
  const handleProjectClick = (project: { id: string; name: string } | null) => {
    if (project === null) {
      // Selecting root level
      setCurrentProjectId(null);
      setNavigationStack([]);
    } else {
      // Navigate into project
      setNavigationStack((prev) => [...prev, project]);
      setCurrentProjectId(project.id);
    }
  };

  /**
   * Navigate back one level in the hierarchy
   */
  const handleNavigateBack = () => {
    setNavigationStack((prev) => prev.slice(0, -1));
    const newParent = navigationStack[navigationStack.length - 2];

    if (newParent) {
      setCurrentProjectId(newParent.id);
    } else {
      setCurrentProjectId(null);
    }
  };

  /**
   * Navigate directly to a specific ancestor by stack index (-1 = root/home)
   */
  const navigateToIndex = (index: number) => {
    if (index === -1) {
      setCurrentProjectId(null);
      setNavigationStack([]);
    } else {
      const newStack = navigationStack.slice(0, index + 1);
      setNavigationStack(newStack);
      setCurrentProjectId(newStack[newStack.length - 1]?.id ?? null);
    }
  };

  /**
   * Reset navigation to root level
   */
  const resetNavigation = () => {
    setCurrentProjectId(null);
    setNavigationStack([]);
  };

  return {
    // Current state
    currentProjectId,
    navigationStack,
    currentParent,

    // Data
    projects,
    lessons,
    projectsLoading,
    lessonsLoading,
    isEmpty,
    hasMoreProjects,
    hasMoreLessons,

    // Actions
    handleProjectClick,
    handleNavigateBack,
    navigateToIndex,
    resetNavigation,
    loadMoreProjects,
    loadMoreLessons,
  };
}
